import React, { useMemo, useRef, useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { useCombobox, useMultipleSelection } from "downshift";
import { useMenuPosition } from "../hooks";

// Convert item to plain label string (used for announcements and filtering)
const itemToString = (item) => (item ? item.label || "" : "");

// Utility to merge multiple refs into one callback ref
const mergeRefs = (...refs) => (node) => {
  refs.forEach((ref) => {
    if (typeof ref === "function") {
      ref(node);
    } else if (ref !== null && ref !== undefined) {
      ref.current = node;
    }
  });
};

// Ensure downshift's ARIA live region exists before first announcement.
// VoiceOver doesn't monitor a live region until it's been in the DOM briefly,
// so we pre-create it on first component mount.
const ensureA11yStatusDiv = () => {
  if (typeof document === "undefined") return;
  if (document.getElementById("a11y-status-message")) return;
  
  const statusDiv = document.createElement("div");
  statusDiv.setAttribute("id", "a11y-status-message");
  statusDiv.setAttribute("role", "status");
  statusDiv.setAttribute("aria-live", "polite");
  statusDiv.setAttribute("aria-relevant", "additions text");
  Object.assign(statusDiv.style, {
    border: "0",
    clip: "rect(0 0 0 0)",
    height: "1px",
    margin: "-1px",
    overflow: "hidden",
    padding: "0",
    position: "absolute",
    width: "1px",
  });
  document.body.appendChild(statusDiv);
};

// State change types that indicate item removal
const REMOVAL_TYPES = new Set([
  useMultipleSelection.stateChangeTypes.DropdownKeyDownBackspace,
  useMultipleSelection.stateChangeTypes.SelectedItemKeyDownBackspace,
  useMultipleSelection.stateChangeTypes.SelectedItemKeyDownDelete,
  useMultipleSelection.stateChangeTypes.FunctionRemoveSelectedItem,
]);

/**
 * DownshiftJS-based combobox for filter selector dropdowns
 */
export const Combobox = ({
  id,
  labelId,
  ariaLabel,
  options,
  selected = [],
  onTagAdded,
  onTagRemoved,
  labels = {},
  name,
  placeholder = "",
  emptyLabel = "No items",
  inputClassName = "",
}) => {
  const inputRef = useRef(null);
  const menuRef = useRef(null);
  const [inputValue, setInputValue] = useState("");

  // Pre-create downshift's ARIA live region on mount so VoiceOver can monitor it
  useEffect(() => {
    ensureA11yStatusDiv();
  }, []);

  const filteredItems = useMemo(() => {
    const lowerInput = (inputValue || "").toLowerCase().trim();
    if (!lowerInput) return options;
    return options.filter((option) => option.label?.toLowerCase().includes(lowerInput));
  }, [options, inputValue]);

  // Convert selected IDs to option objects
  const selectedToItems = useCallback((selectedIds) => {
    return selectedIds.map((tagId) => {
      const stringId = String(tagId);
      const option = options.find((opt) => String(opt.value) === stringId);
      return option || { value: stringId, label: labels[stringId] || stringId };
    });
  }, [options, labels]);

  // Initial selected items from props (computed once on mount, never changes)
  const [initialSelectedItems] = useState(() => selectedToItems(selected));

  // Track the last removed item for ARIA announcement
  const lastRemovedItemRef = useRef(null);

  // Provide accessible removal announcement (required since downshift v9)
  const getA11yRemovalMessage = useCallback(() => {
    const removedItem = lastRemovedItemRef.current;
    if (removedItem) {
      return `${itemToString(removedItem)} has been removed.`;
    }
    return "";
  }, []);

  // useMultipleSelection manages selected items and keyboard navigation
  const {
    getSelectedItemProps,
    getDropdownProps,
    selectedItems,
    addSelectedItem,
    removeSelectedItem,
    setSelectedItems,
  } = useMultipleSelection({
    initialSelectedItems,
    getA11yStatusMessage: getA11yRemovalMessage,
    onStateChange: ({ selectedItems: newSelectedItems, type }) => {
      if (!newSelectedItems) return;
      
      if (REMOVAL_TYPES.has(type)) {
        // Find and announce the removed item
        const removedItem = selectedItems.find(
          (item) => !newSelectedItems.some((newItem) => String(newItem.value) === String(item.value))
        );
        if (removedItem) {
          lastRemovedItemRef.current = removedItem;
          if (onTagRemoved) onTagRemoved(String(removedItem.value));
        }
      } else if (type === useMultipleSelection.stateChangeTypes.FunctionAddSelectedItem) {
        // Clear stale removal data so we don't re-announce old removals
        lastRemovedItemRef.current = null;
        // Find and notify parent of the added item
        const addedItem = newSelectedItems.find(
          (newItem) => !selectedItems.some((item) => String(item.value) === String(newItem.value))
        );
        if (addedItem && onTagAdded) {
          onTagAdded(String(addedItem.value));
        }
      } else {
        // Clear stale announcement data for other state changes
        lastRemovedItemRef.current = null;
      }
    },
  });

  // Sync external prop changes to internal state
  // Only runs when the external `selected` prop changes (not internal state)
  const prevSelectedRef = useRef(selected);
  useEffect(() => {
    const prevIds = prevSelectedRef.current.map(String).sort().join(",");
    const currentIds = selected.map(String).sort().join(",");
    
    // Only sync when external prop actually changed
    if (prevIds !== currentIds) {
      // Check if internal state differs from external prop
      const internalIds = selectedItems.map((item) => String(item.value)).sort().join(",");
      if (internalIds !== currentIds) {
        setSelectedItems(selectedToItems(selected));
      }
      prevSelectedRef.current = selected;
    }
  }, [selected, selectedItems, selectedToItems, setSelectedItems]);

  // Memoized Set of selected IDs for faster lookup
  const selectedIdSet = useMemo(
    () => new Set(selectedItems.map((item) => String(item.value))),
    [selectedItems]
  );

  // Handle item selection/deselection from menu
  const handleSelectItem = useCallback((item) => {
    if (selectedIdSet.has(String(item.value))) {
      removeSelectedItem(item);
    } else {
      addSelectedItem(item);
    }
  }, [selectedIdSet, addSelectedItem, removeSelectedItem]);

  const {
    isOpen,
    getMenuProps,
    getInputProps,
    getItemProps,
    highlightedIndex,
  } = useCombobox({
    id,
    labelId,
    items: filteredItems,
    itemToString,
    inputValue,
    defaultHighlightedIndex: 0,
    selectedItem: null, // Always null - we manage selection via useMultipleSelection
    onInputValueChange: ({ inputValue: newValue }) => {
      setInputValue(newValue || "");
    },
    onSelectedItemChange: ({ selectedItem }) => {
      if (!selectedItem) return;
      handleSelectItem(selectedItem);
      setInputValue("");
    },
    stateReducer: (state, actionAndChanges) => {
      const { type, changes } = actionAndChanges;
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          return {
            ...changes,
            inputValue: "",
            highlightedIndex: state.highlightedIndex,
          };
        case useCombobox.stateChangeTypes.InputBlur:
          // do not select on tab keypress when dropdown menu is open and an item was selected with arrow keys
          // overrides default downshiftjs behavior (weird...)
          if (changes.selectedItem) {
            return {
              ...changes,
              inputValue: "",
              selectedItem: null,
            }
          }

          return changes;
        default:
          return changes;
      }
    },
  });

  const menuPosition = useMenuPosition({ anchorRef: inputRef, isOpen });

  const inputProps = getInputProps(
    getDropdownProps({ preventKeyAction: isOpen })
  );
  // inputProps.ref is actually a callback function and not 100% a "ref"
  // so we need to run the ref callback on both their cb and our own ref (needed for menu position above)
  const inputRefs = mergeRefs(inputRef, inputProps.ref);

  return (
    <div className="em-combobox-content-container">
      <div className="em-combobox-content-container-dropdown">
        <input
          {...inputProps}
          ref={inputRefs}
          className={inputClassName}
          placeholder={placeholder}
          type="text"
          aria-label={labelId ? undefined : ariaLabel}
        />
        <ul
          {...getMenuProps({
            ref: menuRef,
            className: classNames("em-combobox-menu", {
              "em-combobox-menu-above": menuPosition === "above",
              "em-hidden": !isOpen,
            }),
          })}
        >
          {isOpen && (
            filteredItems.length === 0 ? (
              <li className="em-combobox-menu-empty">{emptyLabel}</li>
            ) : (
              filteredItems.map((item, index) => {
                const isSelected = selectedIdSet.has(String(item.value));
                return (
                  <li
                    key={String(item.value)}
                    {...getItemProps({
                      item,
                      index,
                      className: classNames("em-combobox-menu-item", {
                        highlighted: highlightedIndex === index,
                        selected: isSelected,
                      }),
                    })}
                  >
                    {item.displayLabel || item.label}
                  </li>
                );
              })
            )
          )}
        </ul>
      </div>
      {selectedItems.length > 0 && (
        <div className="em-combobox-selected-items" role="list" aria-label="Selected items">
          {selectedItems.map((item, index) => {
            const tagId = String(item.value);
            const label = item.label || labels[tagId] || tagId;
            return (
              <span
                key={tagId}
                className="em-combobox-selected-items-item"
                role="listitem"
                {...getSelectedItemProps({ selectedItem: item, index })}
              >
                {name && <input type="hidden" name={name} value={tagId} />}
                {label}
                {onTagRemoved && (
                  <button
                    type="button"
                    className="em-combobox-remove-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeSelectedItem(item);
                    }}
                  >
                    <span className="sr-only">Remove {label}</span>
                    <i className="fa fas fa-times" aria-hidden="true" />
                  </button>
                )}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
};

Combobox.propTypes = {
  id: PropTypes.string.isRequired,
  labelId: PropTypes.string,
  ariaLabel: PropTypes.string,
  options: PropTypes.array.isRequired,
  selected: PropTypes.array,
  onTagAdded: PropTypes.func.isRequired,
  onTagRemoved: PropTypes.func,
  labels: PropTypes.object,
  name: PropTypes.string,
  placeholder: PropTypes.string,
  emptyLabel: PropTypes.string,
  inputClassName: PropTypes.string,
};
